import { ChevronRightIcon } from "@chakra-ui/icons";
import { Breadcrumb, BreadcrumbItem, BreadcrumbLink } from "@chakra-ui/react";
import { FC, useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { primaryColor } from "../../../utils/colors";
import { getUsers } from "../../../services/User/getUsers";
import { getOrganization } from "../../../services/getOrganization";
import { getRoleById } from "../../../services/getRoleById";
import { fetchBreadcrumbsData } from "./fetchBreadcrumbsData";
import { getIndicator } from "../../../services/Indicator/getIndicator";
import {
  FORBIDDEN_ROUTE,
  INTERNAL_ERROR_ROUTE,
  NOT_FOUND_ROUTE,
} from "../../../utils/endpoints";

const BreadcrumbsComponent: FC = () => {
  const { pathname } = useLocation();
  const pathSegments = pathname.split("/").filter((segment) => segment !== "");
  const [userName, setUserName] = useState<string | null>(null);
  const [profileName, setProfileName] = useState<string | null>(null);
  const [organizationName, setOrganizationName] = useState<string | null>(null);
  const [indicatorName, setIndicatorName] = useState<string | null>(null);
  const [currentType, setCurrentType] = useState<string | null>(null);

  const getChildName = (pathSegments: string[]): string => {
    if (pathSegments.includes("tabelas-auxiliares")) return "Editar";
    return "";
  };

  const hasChild = pathSegments.length > 1;
  const childName = hasChild ? getChildName(pathSegments) : "";

  const getSegmentLabel = (segment: string, segmentsList: any[]): string => {
    return segmentsList.find((item) => item.name === segment)?.label || segment;
  };

  const getLastSegment = (route: string): string => {
    return route.split("/").at(-1) ?? "";
  };

  const segmentsList = [
    { name: "tabelas-auxiliares", label: "Tabelas auxiliares" },
    { name: "entidades", label: "Entidades" },
    { name: "criar-entidades", label: "Criar Entidade" },
    { name: "perfis", label: "Perfis" },
    { name: "criar-perfis", label: "Criar Perfis" },
    { name: "utilizadores", label: "Utilizadores" },
    { name: "criar-utilizador", label: "Criar Utilizador" },
    { name: "indicadores", label: "Indicadores" },
    { name: "criar-indicadores", label: "Criar Indicadores" },
    {
      name: "carregar-dados-no-sisan",
      label: "Carregar dados no SISAN (Excel)",
    },
    { name: "gestao-de-indicadores", label: "Gestão de indicadores" },
    { name: "minha-conta", label: "Minha Conta" },
    { name: "boletins", label: "Boletins" },
    { name: "ocorrencias-no-terreno", label: "Ocorrências no Terreno" },
    {
      name: "criar-ocorrencias-no-terreno",
      label: "Criar Ocorrências no Terreno",
    },
    { name: "documentos", label: "Documentos" },
    { name: getLastSegment(FORBIDDEN_ROUTE), label: "Acesso negado" },
    { name: getLastSegment(INTERNAL_ERROR_ROUTE), label: "Erro" },
    { name: getLastSegment(NOT_FOUND_ROUTE), label: "Não encontrado" },
  ];

  const getSegmentData = (type: string, segment: string) => {
    if (type !== currentType) {
      setUserName(null);
      setProfileName(null);
      setOrganizationName(null);
      setIndicatorName(null);
      setCurrentType(type);
    }

    const validSegmentNames = new Set(segmentsList.map((item) => item.name));
    if (validSegmentNames.has(segment)) {
      return;
    }

    const typeToFetchMapping: { [key: string]: () => void } = {
      utilizadores: () =>
        fetchBreadcrumbsData(segment, getUsers, setUserName, true),
      entidades: () =>
        fetchBreadcrumbsData(segment, getOrganization, setOrganizationName),
      perfis: () =>
        fetchBreadcrumbsData(segment, getRoleById, setProfileName, true),
      "gestao-de-indicadores": () =>
        fetchBreadcrumbsData(segment, getIndicator, setIndicatorName),
    };

    if (typeToFetchMapping[type]) {
      typeToFetchMapping[type]();
    }
  };

  useEffect(() => {
    const segment = pathSegments[1];
    if (pathSegments.length > 1) {
      getSegmentData(pathSegments[0], segment);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathSegments]);

  const helperSegmentTable = (
    segment: string,
    index: number,
    pathSegments: string[],
    userName: string | null,
    profileName: string | null,
    organizationName: string | null,
    indicatorName: string | null,
    childName: string,
    segmentsList: any[],
  ): string => {
    if (index === 1) {
      if (userName) return userName;
      if (profileName) return profileName;
      if (organizationName) return organizationName;
      if (indicatorName) return indicatorName;
    }

    const isCurrentPage = index + 1 === pathSegments.length;
    if (isCurrentPage && childName) return childName;

    return getSegmentLabel(segment, segmentsList);
  };

  return (
    <Breadcrumb
      spacing="4px"
      separator={<ChevronRightIcon />}
      color={primaryColor}
    >
      <BreadcrumbItem>
        <BreadcrumbLink fontWeight="bold" as={Link} to="/dashboard">
          Painel de Controlo
        </BreadcrumbLink>
      </BreadcrumbItem>
      {pathSegments[0] !== "dashboard" &&
        pathSegments.map((segment, index, array) => {
          if (["gerir", "items", "associar"].includes(segment)) return null;
          const isCurrentPage = index + 1 === array.length;
          const segmentLabel = helperSegmentTable(
            segment,
            index,
            pathSegments,
            userName,
            profileName,
            organizationName,
            indicatorName,
            childName,
            segmentsList,
          );
          const linkProps = isCurrentPage
            ? {}
            : {
                as: Link,
                to: `/${pathSegments.slice(0, index + 1).join("/")}`,
              };

          return (
            <BreadcrumbItem key={index} isCurrentPage={isCurrentPage}>
              <BreadcrumbLink
                {...linkProps}
                fontWeight={isCurrentPage ? "normal" : "bold"}
              >
                {segmentLabel}
              </BreadcrumbLink>
            </BreadcrumbItem>
          );
        })}
    </Breadcrumb>
  );
};

export default BreadcrumbsComponent;
